import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FontWeight, Heading } from '@lili/lili-frontend-common';
import { OnboardingFormFields } from '@Types/index';
import { NationalityInput } from '@Components/inputs/NationalityInput';
import { PassportInput } from '@Components/inputs/PassportInput';
import { NationalIdInput } from '@Components/inputs/NationalIdInput';
import { PhoneInput } from '@Components/inputs/Phone';
import { DateOfBirthInput } from '@Components/inputs/DateOfBirthInput';
import { getRouteOrdinal, RoutesMap } from '@Router/router.types';
import { findCountryByCode } from '../addUBOUtils';
import type { UBOFormFields } from '../UBOFormScheme';
import type { Country } from '@Types/country';
import type { RegionCode } from 'google-libphonenumber';

interface UBOIdentityNonUSProps {
  nonUsCountriesList: { title: string; value: string }[];
  phoneCountryCodes: string[];
  countries: Country[];
}

export const UBOIdentityNonUS = ({
  nonUsCountriesList,
  phoneCountryCodes,
  countries,
}: UBOIdentityNonUSProps) => {
  const { t } = useTranslation();
  const { clearErrors, resetField, setError, setValue, getValues, watch } =
    useFormContext<UBOFormFields>();
  const baseTabIndex = getRouteOrdinal(RoutesMap.ADD_OWNER);

  const selectedNationalityCode = watch(OnboardingFormFields.NATIONALITY);
  const selectedNationality = selectedNationalityCode
    ? findCountryByCode(selectedNationalityCode, countries)
    : undefined;

  return (
    <div className='w-full'>
      <Heading.H8 fontWeight={FontWeight.MEDIUM} className='mb-[16px]'>
        {t('pages.addOwner.identitySection.title')}
      </Heading.H8>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-[24px]'>
        <div className='w-full h-[80px]'>
          <NationalityInput
            onNationalityChange={() => {
              clearErrors(OnboardingFormFields.NATIONALITY);
              resetField(OnboardingFormFields.NATIONAL_ID_NUMBER, { defaultValue: '' });
            }}
            selectedCountry={selectedNationality}
            listItems={nonUsCountriesList}
            tabIndex={baseTabIndex + 5}
            onSetError={(errorMsg) => {
              setError(OnboardingFormFields.NATIONALITY, { message: errorMsg });
            }}
          />
        </div>

        <div className='w-full z-[999] mb-2'>
          <PhoneInput
            onIsoCodeChange={(isoCode) => {
              setValue(OnboardingFormFields.PHONE_NUMBER_ISO_CODE, isoCode);
            }}
            initialIsoCode={getValues(OnboardingFormFields.PHONE_NUMBER_ISO_CODE) as RegionCode}
            isSelectMenuDisabled={false}
            filteredCountries={phoneCountryCodes as RegionCode[]}
            tabIndex={baseTabIndex + 6}
          />
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-[24px]'>
        <NationalIdInput
          selectedCountry={selectedNationality}
          tabIndex={baseTabIndex + 7}
          helperText={t('pages.addOwner.identitySection.nationalIdHelper')}
          helperTextClassName='-mt-[24px]'
        />
        <DateOfBirthInput tabIndex={baseTabIndex + 8} />
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-[24px]'>
        <PassportInput tabIndex={baseTabIndex + 9} />
      </div>
    </div>
  );
};
